// Scan result cards for the QR validate screen (VerifyQR / F68).
// Spec: VECINDUS_FUNCTION_SPEC.md ADDENDUM 2026-09-08 (c) §2 + §5 Rulings 2–3.
//
// Three entry cases (scanner roles: admin / sub_admin / seguridad):
//  - RESIDENT: full identity-at-the-gate card — name + photo + unit/address.
//  - EMPLOYEE: employee profile info + destination address.
//    HARD RULE (Jaime 11:58): employees NEVER see who lives in a unit — no
//    resident identity fields are ever rendered on employee or visitor cards
//    (this component strips them defensively, server must filter too).
//  - VISITOR: visitor name + destination address + prominent validity state.
//    Registered visitor with filled profile → full profile (photo/phone/email).
//    Unregistered visitor → name/destination/validity + ID-capture prompt.
//
// The backend payload contract is owned by the backend lane (task
// qr_scan_cards_backend). This component is DEFENSIVE: every field is looked
// up across the plausible key names the backend may emit and every render
// degrades gracefully when a field is missing.

import { useMemo, useState, type ReactElement } from 'react';
import {
  CheckCircle, XCircle, AlertTriangle, User, Home, Clock,
  Phone, Mail, Car, BadgeCheck, IdCard, Briefcase, UserX,
} from 'lucide-react';
import AuthImage from './AuthImage';

export type ScanCardKind = 'resident' | 'employee' | 'visitor' | 'unknown';
export type ScanCardStatus = 'allowed' | 'denied' | 'expired';

export interface ScanCardModel {
  kind: ScanCardKind;
  status: ScanCardStatus;
  name?: string;
  photoUrl?: string;
  /** Unit/address — resident unit, or destination for employee/visitor. */
  address?: string;
  phone?: string;
  email?: string;
  /** Invite vehicle plate (BUG 3, 2026-09-15): shown on visitor cards. */
  vehiclePlate?: string;
  /** Visitor registered with a filled profile (Ruling 3(a)). */
  registered?: boolean;
  /** Backend-flagged requirement to scan the visitor's ID document (Ruling 3(b)). */
  idScanRequired?: boolean;
  /** Raw invalid/expired reason from the backend, when present. */
  reason?: string;
  /** Scan direction from the payload (BackendDev 2026-09-09); falls back to
      the direction the scanner selected in the UI. Omitted when unknown. */
  direction?: 'entrada' | 'salida';
  /** expired_exit: SALIDA scan registered on an already-expired access. */
  expiredExit?: boolean;
  /** Subject already has an ID document on file (has_id_photo / id_photo_url). */
  hasIdPhoto?: boolean;
  idPhotoUrl?: string;
  /** ADDENDUM 2026-09-14b: VQR2 user-identity resolution — 'role' (resident/
      employee membership) or 'invite' (claimed visitor invite). Absent on
      legacy code-scoped scans; only rendered when present. */
  resolutionSource?: 'role' | 'invite';
  /** Code origin (roadmap item E, 2026-09-16): 'walk_in' → "Sin QR" chip.
      Absent on legacy payloads; only rendered when present. */
  origin?: string;
  timestamp: string;
}

// Plausible field aliases the backend may use. First hit wins.
function pick(row: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = row?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return undefined;
}

// HARD RULE enforcement: these keys carry RESIDENT identity and must never be
// read for employee/visitor cards (defense-in-depth; server filters per role).
const RESIDENT_IDENTITY_KEYS = [
  'resident_name', 'owner_name', 'host_name', 'resident_photo',
  'resident_photo_url', 'owner_photo', 'host_photo', 'resident_unit_occupant',
];

export function normalizeScanCard(
  row: Record<string, unknown> | undefined | null,
  opts: { status?: ScanCardStatus; reason?: string; timestamp?: string; direction?: 'entrada' | 'salida' } = {},
): ScanCardModel {
  const raw = (row ?? {}) as Record<string, unknown>;
  const rawType = pick(raw, ['entry_type', 'card_type', 'scan_type', 'type', 'code_type']) ?? '';
  const t = rawType.toLowerCase();

  let kind: ScanCardKind = 'unknown';
  if (t === 'resident') kind = 'resident';
  else if (t === 'employee' || t === 'staff' || t === 'employee_access') kind = 'employee';
  else if (t === 'visitor' || t === 'guest' || t === 'provider' || t === 'service') kind = 'visitor';

  // Strip resident-identity keys for non-resident cards (HARD RULE, defense-in-depth).
  const r: Record<string, unknown> =
    kind === 'resident'
      ? raw
      : (() => { const c = { ...raw }; for (const k of RESIDENT_IDENTITY_KEYS) delete c[k]; return c; })();

  const valid = opts.status ? opts.status === 'allowed' : r.valid === true;
  const reason = opts.reason ?? pick(r, ['reason', 'invalid_reason', 'deny_reason']);

  // Validity: expired status wins if explicitly signaled, else derived from valid flag.
  let status: ScanCardStatus = valid ? 'allowed' : 'denied';
  if (opts.status) status = opts.status;
  else if (!valid && /expired|expirad/i.test(reason ?? '')) status = 'expired';

  const name =
    kind === 'resident'
      ? pick(r, ['name', 'full_name', 'resident_display_name', 'display_name', 'user_name'])
      : kind === 'employee'
        ? pick(r, ['employee_name', 'name', 'full_name', 'display_name', 'user_name'])
        : pick(r, ['visitor_name', 'name', 'full_name', 'display_name', 'guest_name']);

  const photoUrl =
    kind === 'resident'
      ? pick(r, ['photo', 'photo_url', 'avatar_url', 'profile_photo', 'picture'])
      : kind === 'employee'
        ? pick(r, ['employee_photo', 'photo', 'photo_url', 'avatar_url', 'profile_photo', 'picture'])
        : pick(r, ['visitor_photo', 'visitor_photo_url', 'photo', 'photo_url', 'avatar_url', 'profile_photo', 'picture']);

  const address =
    kind === 'resident'
      ? pick(r, ['unit', 'unit_label', 'unit_address', 'address', 'unit_number'])
      : pick(r, ['destination_address', 'destination', 'dest_address', 'address', 'unit', 'unit_label']);

  const profileFilled =
    r.profile_filled === true ||
    r.has_profile === true ||
    Boolean(name && (photoUrl || phoneOf(r) || emailOf(r)));

  const registered =
    typeof r.registered === 'boolean'
      ? r.registered
      : typeof r.visitor_registered === 'boolean'
        ? r.visitor_registered
        : kind === 'visitor'
          ? profileFilled
          : undefined;

  // Subject already has an ID on file; a previous capture must suppress the
  // "sin perfil: escanear doc" prompt even when the backend's legacy
  // id_capture_required flag remains true.
  const hasIdPhoto =
    r.has_id_photo === true ||
    r.hasIdPhoto === true ||
    Boolean(pick(r, ['id_photo_url', 'id_photo', 'id_document_url', 'id_document']));
  const idPhotoUrl = pick(r, ['id_photo_url', 'id_document_url']);
  const idScanRequired =
    kind === 'visitor' && !hasIdPhoto &&
    (r.id_scan_required === true ||
      r.requires_id_scan === true ||
      r.id_capture_required === true ||
      (registered === false && r.id_scan_required !== false));

  // Direction (2026-09-09 contract): payload value wins; fall back to the
  // direction the scanner selected in the UI (opts.direction).
  const rawDir = (pick(r, ['direction', 'scan_direction', 'dir']) ?? '').toLowerCase();
  const direction: 'entrada' | 'salida' | undefined =
    rawDir === 'entrada' || rawDir === 'in' || rawDir === 'entry'
      ? 'entrada'
      : rawDir === 'salida' || rawDir === 'out' || rawDir === 'exit'
        ? 'salida'
        : opts.direction;

  // expired_exit → "SALIDA registrada (acceso expirado)" amber note.
  const expiredExit = r.expired_exit === true || r.expiredExit === true;
  return {
    kind,
    status,
    name,
    photoUrl,
    address,
    phone: phoneOf(r),
    email: emailOf(r),
    vehiclePlate: pick(r, ['vehicle_plate', 'vehiclePlate']),
    registered,
    idScanRequired,
    direction,
    expiredExit,
    hasIdPhoto,
    idPhotoUrl,
    resolutionSource: normalizeResolutionSource(r),
    origin: pick(r, ['origin', 'code_origin', 'codeOrigin']),
    reason,
    timestamp: opts.timestamp ?? new Date().toISOString(),
  };
}

function normalizeResolutionSource(r: Record<string, unknown>): 'role' | 'invite' | undefined {
  const v = (pick(r, ['resolution_source', 'resolutionSource']) ?? '').toLowerCase();
  return v === 'role' || v === 'invite' ? (v as 'role' | 'invite') : undefined;
}

function phoneOf(r: Record<string, unknown>): string | undefined {
  return pick(r, ['phone', 'visitor_phone', 'phone_number', 'mobile']);
}
function emailOf(r: Record<string, unknown>): string | undefined {
  return pick(r, ['email', 'visitor_email']);
}

const STATUS_STYLES: Record<ScanCardStatus, { bar: string; chip: string; label: string }> = {
  allowed: { bar: 'bg-emerald-500', chip: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', label: '✅ Acceso Permitido' },
  denied: { bar: 'bg-red-500', chip: 'bg-red-500/20 text-red-300 border-red-500/40', label: '❌ Acceso Denegado' },
  expired: { bar: 'bg-amber-500', chip: 'bg-amber-500/20 text-amber-300 border-amber-500/40', label: '⚠️ Código Expirado' },
};

const KIND_LABEL: Record<ScanCardKind, string> = {
  resident: 'Residente',
  employee: 'Empleado',
  visitor: 'Visitante',
  unknown: 'Acceso',
};

export default function ScanResultCard({ card }: { card: ScanCardModel }) {
  const s = STATUS_STYLES[card.status];
  const isVisitor = card.kind === 'visitor';
  const showFullProfile = isVisitor && card.registered === true;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Full visitor profile per Ruling 3(a): only when registered w/ filled profile.
  const visitorDetails = showFullProfile
    ? [card.phone && { icon: <Phone className="w-4 h-4" />, text: card.phone },
       card.email && { icon: <Mail className="w-4 h-4" />, text: card.email },
       card.vehiclePlate && { icon: <Car className="w-4 h-4" />, text: `Placa: ${card.vehiclePlate}` }].filter(Boolean) as { icon: ReactElement; text: string }[]
    : [];

  const initials = (card.name || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <section
      data-testid="scan-result-card"
      className={`rounded-2xl overflow-hidden mb-6 bg-gray-800 border ${card.status === 'allowed' ? 'border-emerald-500/40' : card.status === 'expired' ? 'border-amber-500/40' : 'border-red-500/40'}`}
      aria-live="polite"
    >
      {/* Prominent validity banner */}
      <div className={`${s.bar} px-6 py-3 flex items-center justify-between`}>
        <span className="text-white font-bold text-lg">{s.label}</span>
        <span className={`text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full border ${s.chip}`}>
          {card.status === 'allowed' ? 'Válido' : card.status === 'expired' ? 'Expirado' : 'Inválido'}
        </span>
      </div>

      {/* Direction banner (BackendDev 2026-09-09): state clearly whether this
          scan was an ENTRADA or SALIDA. expired_exit → amber "SALIDA
          registrada (acceso expirado)". Omitted when direction is unknown. */}
      {card.direction && (
        <div
          data-testid="scan-direction-banner"
          className={`px-6 py-2 flex items-center gap-2 text-sm font-bold border-b ${
            card.expiredExit
              ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
              : 'bg-gray-800 text-gray-200 border-gray-700'
          }`}
        >
          {card.expiredExit ? (
            <>⚠️ SALIDA registrada (acceso expirado)</>
          ) : card.direction === 'entrada' ? (
            <>{card.status === 'allowed' ? '⬆ ENTRADA permitida' : '⬆ ENTRADA denegada'}</>
          ) : (
            <>{card.status === 'allowed' ? '⬇ SALIDA registrada' : '⬇ SALIDA denegada'}</>
          )}
        </div>
      )}

      {/* Resolution source badge (ADDENDUM 2026-09-14b): shows HOW this card
          was resolved when the scan came from a VQR2 user-identity pass —
          by condo role (Residente/Empleado) or by claimed invite (Invitación).
          Legacy code-scoped scans carry no resolution_source: no badge. */}
      {card.resolutionSource && (
        <div
          data-testid="scan-resolution-badge"
          className="px-6 py-1.5 flex items-center gap-2 text-xs font-semibold bg-gray-800 text-slate-300 border-b border-gray-700"
        >
          <BadgeCheck className="w-3.5 h-3.5 text-[var(--v-teal-400)]" />
          {card.resolutionSource === 'role' ? 'Resuelto por membresía en este condominio' : 'Resuelto por invitación activa'}
        </div>
      )}

      <div className="p-6">
        {/* Identity row */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0">
            {card.photoUrl ? (
              <img
                src={card.photoUrl}
                alt={card.name ? `Foto de ${card.name}` : 'Foto de perfil'}
                className="w-full h-full object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <User className="w-8 h-8 text-gray-500" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-white text-xl font-bold truncate">
              {card.name || (card.status === 'allowed' ? 'Usuario' : 'Código Inválido')}
            </h3>
            <p className="text-gray-400 text-sm flex items-center gap-1.5">
              {card.kind === 'employee' ? <Briefcase className="w-4 h-4" /> : <BadgeCheck className="w-4 h-4" />}
              {KIND_LABEL[card.kind]}
              {isVisitor && card.registered === false && (
                <span className="ml-1 text-amber-400">· No registrado</span>
              )}
              {/* Roadmap item E: guard-registered walk-in → "Sin QR" chip. */}
              {card.origin === 'walk_in' && (
                <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-violet-500/25 text-violet-300 align-middle">
                  Sin QR
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Destination / unit address */}
        {card.address && (
          <p className="mt-4 text-gray-300 flex items-center gap-2 text-sm">
            <Home className="w-4 h-4 text-[#36CCD8] flex-shrink-0" />
            <span className="truncate">
              {card.kind === 'resident' ? 'Unidad: ' : 'Destino: '}
              {card.address}
            </span>
          </p>
        )}

        {/* Registered visitor full profile (Ruling 3(a)) */}
        {visitorDetails.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {visitorDetails.map((d, i) => (
              <p key={i} className="text-gray-400 flex items-center gap-2 text-sm">
                {d.icon}
                <span className="truncate">{d.text}</span>
              </p>
            ))}
          </div>
        )}

        {isVisitor && card.idPhotoUrl && (
          <div className="mt-4 rounded-xl bg-gray-900/60 border border-gray-700 p-3">
            <p className="text-gray-300 text-xs mb-2">Documento capturado (más reciente)</p>
            <AuthImage url={card.idPhotoUrl} alt="Documento de identidad capturado"
              className="w-28 h-20 object-cover rounded-lg cursor-zoom-in hover:opacity-90"
              onClick={() => setLightboxOpen(true)} />
          </div>
        )}
        {isVisitor && card.hasIdPhoto && !card.idPhotoUrl && (
          <p className="mt-3 text-emerald-300 text-xs">✓ Documento de identidad registrado</p>
        )}

        {/* Unregistered visitor: mandatory ID-capture prompt (Ruling 3(b)) */}
        {isVisitor && card.idScanRequired && (
          <div className="mt-4 rounded-xl bg-amber-500/10 border border-amber-500/40 px-4 py-3 flex items-start gap-3">
            <IdCard className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-200 text-sm">
              Visitante sin perfil: escanear documento de identidad y guardarlo en el registro de visitas.
              <span className="block text-amber-400/70 text-xs mt-0.5">
                Retención ~30 días (valor por defecto, sujeto a confirmación).
              </span>
            </p>
          </div>
        )}

        {/* Deny reason / fallback for missing fields */}
        {card.status !== 'allowed' && card.reason && (
          <p className="mt-3 text-sm text-gray-400">
            {card.kind === 'unknown' && !card.name ? <UserX className="w-4 h-4 inline mr-1 -mt-0.5" /> : null}
            Motivo: {card.reason}
          </p>
        )}

        <p className="mt-4 pt-4 border-t border-gray-700 text-gray-500 text-xs flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(card.timestamp).toLocaleTimeString('es-MX')}
          {initials && <span className="ml-auto opacity-40">{initials}</span>}
        </p>
      </div>
      {lightboxOpen && card.idPhotoUrl && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" role="dialog" aria-label="Documento ampliado" onClick={() => setLightboxOpen(false)}>
          <AuthImage url={card.idPhotoUrl} alt="Documento de identidad ampliado" className="max-w-full max-h-[90vh] object-contain rounded-xl" />
          <button type="button" className="absolute top-4 right-4 text-white text-3xl" aria-label="Cerrar" onClick={() => setLightboxOpen(false)}>×</button>
        </div>
      )}
    </section>
  );
}

/** Helper: memoized card model from a raw validate-response row. */
export function useScanCard(
  row: Record<string, unknown> | undefined | null,
  opts: { status?: ScanCardStatus; reason?: string; timestamp?: string },
): ScanCardModel {
  return useMemo(() => normalizeScanCard(row, opts), [row, opts.status, opts.reason, opts.timestamp]);
}

// Re-export icons used by the consumer for consistency (CheckCircle/XCircle/AlertTriangle)
export { CheckCircle, XCircle, AlertTriangle };
